import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ToastProvider } from '../context/ToastContext';
import { VipMpSessionProvider, useVipMpSession } from '../context/VipMpSessionContext';
import { AdobeLoginView } from '../features/auth/AdobeLoginView';
import { PartnerLayout } from '../features/partner/PartnerLayout';
import { PartnerSubmissionsView } from '../features/partner/PartnerSubmissionsView';
import { PartnerSubmitView } from '../features/partner/PartnerSubmitView';
import { getApiBase } from '../api/getApiBase';
import { SessionExpiryBanner } from '../shared/components/SessionExpiryBanner';
import { NotAuthorizedModal } from '../shared/components/NotAuthorizedModal';

function DefaultRedirect() {
  return <Navigate to="/partner/submit" replace />;
}

function AppShell() {
  return (
    <Routes>
      <Route path="/" element={<DefaultRedirect />} />

      <Route path="/partner" element={<PartnerLayout />}>
        <Route index element={<Navigate to="submit" replace />} />
        <Route path="submit" element={<PartnerSubmitView />} />
        <Route path="submissions" element={<PartnerSubmissionsView />} />
      </Route>

      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
}

function AuthGate() {
  const { authStatus, authExpiresAt, loginWithAdobe } = useVipMpSession();
  const [sessionJustExpired, setSessionJustExpired] = useState(false);
  const onExpired = useCallback(() => setSessionJustExpired(true), []);

  const [authError, setAuthError] = useState<'not_enrolled' | 'not_authorized' | 'crm_unavailable' | null>(() => {
    const err = new URLSearchParams(window.location.search).get('error');
    if (err === 'not_enrolled' || err === 'not_authorized' || err === 'crm_unavailable') return err;
    return null;
  });

  useEffect(() => {
    if (authError) {
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.toString());
    }
  }, [authError]);

  useEffect(() => {
    const handler = () => setSessionJustExpired(true);
    window.addEventListener('vip-mp:session-expired', handler);
    return () => window.removeEventListener('vip-mp:session-expired', handler);
  }, []);

  if (authError) {
    return <NotAuthorizedModal errorType={authError} onLoginClick={() => { setAuthError(null); loginWithAdobe(); }} />;
  }

  if (authStatus === 'loading') {
    return (
      <div className="adobe-auth-shell">
        <div className="adobe-auth-card">
          <h1>Checking Adobe session</h1>
          <p>Loading your secure session from the backend...</p>
        </div>
      </div>
    );
  }

  if (authStatus !== 'authenticated' && !sessionJustExpired) {
    return <AdobeLoginView />;
  }

  return (
    <>
      {authStatus === 'authenticated' && <AppShell />}
      <SessionExpiryBanner
        expiresAt={authExpiresAt}
        expired={sessionJustExpired}
        onLoginClick={loginWithAdobe}
        onExpired={onExpired}
      />
    </>
  );
}

function AdobeCallbackBridge() {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    const OUR_ERRORS = ['not_enrolled', 'not_authorized', 'crm_unavailable'];
    if (!code && !state && (!error || OUR_ERRORS.includes(error))) return;

    const callbackUrl = new URL(`${getApiBase()}/auth/adobe/callback`);
    params.forEach((value, key) => {
      callbackUrl.searchParams.set(key, value);
    });
    window.location.replace(callbackUrl.toString());
  }, [location.search]);

  return null;
}

export default function App() {
  return (
    <VipMpSessionProvider>
      <ToastProvider>
        <AdobeCallbackBridge />
        <AuthGate />
      </ToastProvider>
    </VipMpSessionProvider>
  );
}
